import { IStore } from 'library/ts/utilities/interfaces/IStore';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';

/**
 * @description Singleton for handling local storage operations
 * @example
 *
 * ```typescript
 * let store:Store = Store.getInstance();
 *
 * // check if local storage is available
 * console.log( store.available );.
 *
 * // Store 'marcus' at 'username'
 * tore.set( 'username', 'marcus' );
 *
 * // Get 'username'
 * store.get( 'username' );
 *
 * // Remove 'username'
 * store.remove( 'username' );
 *
 * // Clear all keys
 * store.clear();
 *
 * // Store an object literal - class uses JSON.stringify under the hood
 * store.set( 'user', { name: 'marcus', likes: 'javascript' } );
 *
 * // Get the stored object - class uses JSON.parse under the hood
 * var user = store.get( 'user' )
 * alert( user.name + ' likes ' + user.likes );
 * ```
 *
 */
export class Store implements IStore {

	private static _instance: IStore = new Store();

	#available: boolean;
	#logger: ILogger;

	constructor() {
		if ( Store._instance ) {
			throw new Error( this.name + ' is a Singleton and can only be accessed through ' + this.name + '.getInstance()' );
		}
		Store._instance = this;
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', this.name + ':' ] } );

		// check if localStorage is available
		const test: string = 'test' + new Date().getTime();

		try {
			localStorage.setItem( test, test );
			localStorage.removeItem( test );
			this.#available = true;
		} catch ( error ) {
			this.#available = false;
		}
	}

	get name(): string {
		return 'Store';
	}

	/**
	 * @description Returns local storage availability flag.
	 * @return {boolean}
	 */
	get available(): boolean {
		return this.#available;
	}

	/**
	 * @description Returns reference to global Utilities
	 * @returns {Utilities | undefined}
	 */
	get utils() {
		return window?.PARA?.PlayerServices?.Utilities;
	}

	public static getInstance(): IStore {
		return Store._instance;
	}

	/**
	 * @description Sets local storage for a key
	 * @param {string} key
	 * @param value
	 */
	set = ( key: string, value: any ): void => {
		try {
			localStorage.setItem( key, JSON.stringify( value ) );
		} catch ( error ) {
			this.#logger.warn( 'set:', value, 'ERROR:', error );
		}
	};

	/**
	 * @description Gets local storage value for a key
	 * @param {string} key
	 * @return {any | undefined}
	 */
	get = ( key: string ): any | undefined => {
		let value;

		try {
			value = localStorage.getItem( key );
		} catch ( error ) {
			this.#logger.warn( `get: ${key}: ${value}`, 'ERROR:', error );
		}

		if ( !this.utils.Type.isString( value ) ) {
			return undefined;
		}

		try {
			return JSON.parse( value );
		} catch ( error ) {
			return value || undefined;
		}
	};

	/**
	 * @description Removes local storage item by key
	 * @param {string} key
	 */
	remove = ( key: string ): void => {
		try {
			localStorage.removeItem( key );
		} catch ( error ) {
			this.#logger.warn( 'remove:', key, 'ERROR:', error );
		}
	};

	/**
	 * @description Clears local storage
	 */
	clear = (): void => {
		try {
			localStorage.clear();
		} catch ( error ) {
			this.#logger.warn( 'clear:', 'ERROR:', error );
		}
	};

}